import styled, { css } from 'styled-components';
import Pill from '@99designs/frontend-components/components/Pill/Pill';
import { RelativeTime } from '@99designs/frontend-components/components/RelativeTime';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { boxShadows, media, spaces } from '../../lib/styles';
import Stack from '../../spa/core-components/Stack';
import Heading from '../Heading';
import Image from '../Image';
import { HeadingLink } from '../Link';
import Paragraph from '../Paragraph';

export interface Props {
    title: string;
    url: string;
    thumbnailUrl: string;
    channel?: string;
    publishedAt?: string; // TODO: Was DateSource. Need to fix typing.
    readTimeMinutes: number;
    horizontal?: boolean;
}

const ImageWrapper = styled.a`
    display: inline-block;
    text-decoration: none;
    flex-shrink: 0;

    &:focus,
    &:hover {
        box-shadow: ${boxShadows.medium};
        transform: translateY(-2%);
        transition: 0.3s ease-out;
    }

    @media ${media('small')} {
        max-width: none;
    }
`;

const StyledBlogPost = styled.div<{ horizontal: boolean }>((props) => {
    if (props.horizontal) {
        return css`
            display: flex;
            align-items: center;

            @media ${media('small')} {
                flex-direction: column;
                align-items: flex-start;
            }

            ${ImageWrapper} {
                margin-right: ${spaces.space6}px;

                @media ${media('small')} {
                    margin-bottom: ${spaces.space6}px;
                }
            }
        `;
    } else {
        return css`
            ${ImageWrapper} {
                margin-bottom: ${spaces.space6}px;
            }
        `;
    }
});

export default function BlogPost({
    title,
    url,
    thumbnailUrl,
    channel,
    publishedAt,
    readTimeMinutes,
    horizontal = false,
}: Props) {
    const imgWidth = horizontal ? 272 : 393;
    const imgHeight = imgWidth * 0.6;

    return (
        <StyledBlogPost horizontal={horizontal}>
            <ImageWrapper href={url}>
                <Image
                    src={thumbnailUrl}
                    alt={formatMessage(__('Featured image for the article "{postTitle}"'), {
                        postTitle: title,
                    })}
                    width={imgWidth}
                    height={imgHeight}
                />
            </ImageWrapper>
            <Stack space="space5">
                {channel && <Pill>{channel}</Pill>}
                <Heading type="h4" variations={['h6', 'no-margin']}>
                    <HeadingLink href={url}>{title}</HeadingLink>
                </Heading>
                <Paragraph variations={['soft', 'small', 'no-margin']}>
                    {publishedAt && (
                        <>
                            <RelativeTime date={publishedAt} />
                            {' | '}
                        </>
                    )}
                    {formatMessage(__('{readTimeMinutes} min read'), {
                        readTimeMinutes: readTimeMinutes,
                    })}
                </Paragraph>
            </Stack>
        </StyledBlogPost>
    );
}
